/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module PoolModule
 */

import {
    Inject,
    Injectable,
} from '@angular/core';

import {
    anyIPAnyIPPortAnyIPRange,
    anyIPAnyIPRange,
    anyIPPort,
    anyIPRange,
    hostname,
    ip,
    ipAddrWithPortWG,
    ipv6,
    ipv6Port,
} from 'ng/utils/regex.utils';

import {
    IpAddrType,
    IServer,
} from 'generated-types';

import {
    getIpAddr,
    parseIp,
} from 'ng/shared/utils/ip-parser.utils';
import { Observable, Subject } from 'rxjs';

import { ClrLoadingState } from '@cds/core/button';

import { IP_TYPE_ANY } from 'ajs/js/constants/DNS.constants';

interface IDnsLookUpDelayed {
    promise: Promise<string[]>,
    cancel: () => {},
}

/**
 * Service used by pool modal and pool related features.
 * @author Rachit Aggarwal
 */
@Injectable({
    providedIn: 'root',
})
export class PoolServersService {
    /**
     * Resolutions observable for template.
     */
    public resolutions$: Observable<string[]>;

    /**
     * state of the Add server button.
     */
    public loadingState: ClrLoadingState = ClrLoadingState.default;

    /**
     * List of resolutions.
     */
    private resolutions : string[] = [];

    /**
     * Subject for resolutions.
     */
    private resolutionsSubject = new Subject<string[]>();

    /**
     * Stores the promise call.
     */
    private resolutionCall: IDnsLookUpDelayed | null = null;

    constructor(
        @Inject('RangeParser')
        private readonly rangeParser: any,
        @Inject('DNS')
        private dns: any,
    ) {
        this.resolutions$ = this.resolutionsSubject.asObservable();
    }

    /**
     * Converts IP address, range or domain name into the number of underlying IPs. To be
     * passed into reduce.
     */
    public serverStrToNumberOfIPs = (acc: number, serverStr: string): number => {
        if (anyIPAnyIPRange.test(serverStr)) {
            acc += this.rangeParser.getNumberOfIpsFromRange(serverStr);
        } else if (anyIPPort.test(serverStr)) {
            acc++;
        } else if (hostname.test(serverStr)) {
            acc += this.resolutions.length || 1;
        }

        return acc;
    };

    /**
     * Method to clear the resolutions list.
     */
    public clearResolutions() : void {
        this.setResolutions([]);
    }

    /**
     * Function to be passed into map to convert strings into list of ServerConfigs.
     */
    public serverStrToServers = (servers: IServer[], serverStr: string): IServer[] => {
        let chunks;

        if (anyIPRange.test(serverStr)) {
            servers.push(
                ...this.rangeParser.getIpsFromRange(serverStr).map((ip: string) => ({ ip })),
            );
        } else if (ipAddrWithPortWG.exec(serverStr)) {
            chunks = ipAddrWithPortWG.exec(serverStr);
            servers.push({
                ip: getIpAddr(chunks[1]),
                port: +chunks[2],
            });
        } else if (ipv6Port.exec(serverStr)) {
            chunks = ipv6Port.exec(serverStr);
            servers.push({
                ip: getIpAddr(chunks[2], IpAddrType.V6),
                port: +chunks[3],
            });
        } else if (ip.test(serverStr)) {
            servers.push({
                ip: getIpAddr(serverStr, IpAddrType.V4),
            });
        } else if (ipv6.test(serverStr)) {
            servers.push({
                ip: getIpAddr(serverStr, IpAddrType.V6),
            });
        } else if (hostname.test(serverStr)) {
            const hostname = serverStr;

            if (!this.resolutions.length) {
                servers.push({
                    hostname,
                    ip: getIpAddr('0.0.0.0'),
                });
            } else {
                this.resolutions.forEach(ip => servers.push({
                    hostname,
                    ip: parseIp(ip),
                }));
            }
        }

        return servers;
    };

    /**
     * IpAddr/Range/Dns input change handler.
     */
    public onIpAddrRangeDnsChange(cloudId: string, serversStr: string): void {
        this.stopResolution();
        this.clearResolutions();

        if (!anyIPAnyIPPortAnyIPRange.test(serversStr) && hostname.test(serversStr)) {
            this.loadingState = ClrLoadingState.loading;
            this.resolutionCall = this.dns.lookupDelayed(serversStr, cloudId, true, IP_TYPE_ANY);

            this.resolutionCall?.promise.then((ips: string[]) => {
                // TODO: Confirm if second condition required.
                if (ips.length && ips[0] !== serversStr) {
                    this.setResolutions(ips);
                }
            }).finally(() => {
                this.loadingState = ClrLoadingState.default;
                this.resolutionCall = null;
            });
        }
    }

    /**
     * Stops resolution call when field is changed.
     */
    private stopResolution(): void {
        if (this.resolutionCall) {
            this.resolutionCall.cancel();
            this.resolutionCall = null;
        }
    }

    /**
     * Updates local resolutions state and broadcasts list as well.
     */
    private setResolutions(resolutions: string[]): void {
        this.resolutions = resolutions;
        this.resolutionsSubject.next(resolutions);
    }
}
